import { getRuntimeConfig } from '../app/config/runtime-config';
import { getAccessToken } from '../auth/auth-client';
import { getApiCustomerId } from '../auth/customer-context';
import { ApiError } from './errors';
import { mockApiRequest } from './mock-transport';

interface ApiRequestOptions extends RequestInit {
  customerScoped?: boolean;
}

function normalizeError(status: number, body: unknown): ApiError {
  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const nested = record.error && typeof record.error === 'object' ? (record.error as Record<string, unknown>) : {};
  const code = String(nested.code ?? record.error_code ?? 'REQUEST_FAILED');
  const message = String(nested.message ?? record.message ?? 'Route42 could not complete the request.');
  const retryable = Boolean(nested.retryable ?? record.retryable ?? status >= 500);
  const requestId = String(record.request_id ?? '') || undefined;
  return new ApiError({ status, code, message, retryable, requestId, details: body });
}

function redirectSuspendedCustomer(error: ApiError): void {
  if (error.code !== 'CUSTOMER_NOT_ACTIVE' || typeof window === 'undefined') return;
  if (window.location.pathname === '/organization-unavailable') return;
  const customerId = getApiCustomerId();
  if (!customerId) return;
  const target = `/organization-unavailable?organization=${encodeURIComponent(customerId)}`;
  window.location.replace(target);
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const config = getRuntimeConfig();
  if (config.apiMode === 'mock') {
    return mockApiRequest<T>(path, options);
  }

  const token = await getAccessToken();
  if (!token) {
    throw new ApiError({
      status: 401,
      code: 'AUTHENTICATION_REQUIRED',
      message: 'Your Route42 session has expired. Please sign in again.',
      retryable: false,
    });
  }

  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  headers.set('Accept', 'application/json');
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (options.customerScoped !== false) {
    const customerId = getApiCustomerId();
    if (customerId) headers.set('X-Route42-Customer-Id', customerId);
  }

  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    ...options,
    headers,
  });

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = { message: text };
    }
  }
  if (!response.ok) {
    const error = normalizeError(response.status, body);
    redirectSuspendedCustomer(error);
    throw error;
  }
  return body as T;
}
